import Decimal from 'decimal.js';
import { useCallback, useState } from 'react';
import Web3Singleton from 'web3Singleton';

import { parseFromDecimal } from 'utils/parser.utils';

import { StakingPeriod, StakingTableStatus, VipPhaseData } from '../types';
import { FINAL_CALL_THRESHOLD, PERIOD_LABELS, ONE_ADDRESS } from '../constants';
import { determinePhaseStatus, toPeriodDaysEnum } from '../utils';

interface UseVipDataProps {
  walletAddress: string | null;
  tokenDecimals: number;
}

interface UseVipDataReturn {
  vipPhases: VipPhaseData[];
  currentVipPhaseIndex: number;
  fetchVipData: () => Promise<void>;
  resetVipData: () => void;
}

/**
 * Fetches VIP staking program data with user limits, remaining, and per-period eligibilities.
 * Supports multiple phases like periodical staking.
 */
export const useVipData = ({
  walletAddress,
  tokenDecimals,
}: UseVipDataProps): UseVipDataReturn => {
  const lgoWeb3 = Web3Singleton.getInstance();
  const [vipPhases, setVipPhases] = useState<VipPhaseData[]>([]);
  const [currentVipPhaseIndex, setCurrentVipPhaseIndex] = useState(-1);

  /**
   * Resets VIP data (used when wallet disconnects)
   */
  const resetVipData = useCallback(() => {
    setVipPhases([]);
    setCurrentVipPhaseIndex(-1);
  }, []);

  const fetchVipData = useCallback(async () => {
    // Get staking service - prefer wallet-connected, fallback to read-only
    const stakingService = lgoWeb3.getEffectiveStakingService();
    if (!stakingService) {
      return;
    }

    // Use zero address when wallet is not connected to fetch program data
    const addressToUse = walletAddress || ONE_ADDRESS;
    const hasWallet = Boolean(walletAddress);

    try {
      const data = await stakingService.getVipProgramDataWithUserData(addressToUse);
      if (!data) {
        setVipPhases([]);
        setCurrentVipPhaseIndex(-1);
        return;
      }

      const currentPhase = parseInt(data.currentPhase);
      setCurrentVipPhaseIndex(currentPhase);

      // Process all phases (similar to useProgramData)
      const phases: VipPhaseData[] = data.targets.map((targets: string[], phaseIdx: number) => {
        const apys = data.apys[phaseIdx] || [];
        const staked = data.staked[phaseIdx] || [];
        const limits = data.limits[phaseIdx] || [];
        const remainingForUser = data.remaining[phaseIdx] || [];
        const eligibilityForPhase = (data.eligibilities[phaseIdx] || []) as boolean[];
        const requiredWorthForPhase = data.requiredWorth[phaseIdx] || [];
        const status = determinePhaseStatus(phaseIdx, currentPhase);

        // Get the first non-zero requiredWorth value for the phase (usually same for all periods)
        const requiredWorthRaw = requiredWorthForPhase.find((w: string) => w !== '0') || requiredWorthForPhase[0] || '0';
        const requiredWorthDisplay = parseFloat(parseFromDecimal(requiredWorthRaw, tokenDecimals));

        // User is only eligible if wallet is connected AND they pass eligibility check
        const isUserEligible = hasWallet && eligibilityForPhase.some(Boolean);

        const periods: StakingPeriod[] = data.periods.map((days: string, periodIdx: number) => {
          const isEligible = eligibilityForPhase[periodIdx] ?? false;

          const limitValue = limits[periodIdx] || '0';
          const targetValue = targets[periodIdx] || '0';
          const stakedValue = staked[periodIdx] || '0';

          const isLimitGreaterThanTarget = new Decimal(limitValue).gt(targetValue);

          const periodLimit = limits[periodIdx] || '0';
          const periodRemaining = remainingForUser[periodIdx] || '0';

          const target = isLimitGreaterThanTarget ? targetValue : periodLimit;
          const stakedAmount = isLimitGreaterThanTarget ? stakedValue : Decimal.max(0, new Decimal(periodLimit).sub(periodRemaining)).toString();

          const globalRemaining = new Decimal(target).sub(stakedAmount).toString();
          const globalRemainingNum = parseFloat(parseFromDecimal(globalRemaining, tokenDecimals));
          const userRemainingNum = parseFloat(parseFromDecimal(periodRemaining, tokenDecimals));
          const targetNum = parseFloat(parseFromDecimal(target, tokenDecimals));
          const apyValue = parseInt(apys[periodIdx] || '0');

          const isExhausted = globalRemainingNum < 1;
          const isFinalCall =
            !isExhausted && targetNum > 0 && globalRemainingNum / targetNum <= FINAL_CALL_THRESHOLD;
          const userCanStake = hasWallet && isEligible && userRemainingNum > 0 && !isExhausted;
          const displayRemaining = isUserEligible ? globalRemainingNum : targetNum;

          return {
            days: toPeriodDaysEnum(days),
            label: PERIOD_LABELS[days] || `${days} Days`,
            apyDisplay: `${apyValue}%`,
            apyValue,
            target,
            staked: stakedAmount,
            remaining: globalRemaining,
            remainingDisplay: displayRemaining,
            canStake: status === StakingTableStatus.ACTIVE && userCanStake,
            isFinalCall,
            isExhausted,
          };
        });

        const totalTarget = periods.reduce((sum, p) => new Decimal(sum).add(p.target).toString(), '0');
        const totalStakedPhase = periods.reduce((sum, p) => new Decimal(sum).add(p.staked).toString(), '0');
        const remainingCapacity = new Decimal(totalTarget).sub(totalStakedPhase).toString();
        const totalTargetDisplay = parseFloat(parseFromDecimal(totalTarget, tokenDecimals));
        const remainingCapacityDisplayNum = parseFloat(parseFromDecimal(remainingCapacity, tokenDecimals));
        const remainingCapacityDisplay = isUserEligible ? remainingCapacityDisplayNum : totalTargetDisplay;

        // Calculate APY range
        const apyValues = apys.map((a: string) => parseInt(a));
        const minApy = apyValues.length > 0 ? Math.min(...apyValues) : 0;
        const maxApy = apyValues.length > 0 ? Math.max(...apyValues) : 0;

        return {
          phaseIndex: phaseIdx,
          name: `VIP ${phaseIdx + 1}`,
          status,
          apyRange: `${minApy}% - ${maxApy}%`,
          minApy,
          maxApy,
          totalTarget,
          totalStaked: totalStakedPhase,
          remainingCapacity,
          remainingCapacityDisplay,
          canStake: status === StakingTableStatus.ACTIVE && isUserEligible && periods.some((p) => p.canStake),
          periods,
          isUserEligible,
          eligibilityMessage: isUserEligible ? '' : `VIP eligibility requires ${requiredWorthDisplay.toLocaleString()} RMV worth`,
          requiredWorthDisplay,
          userRemainingPerPeriod: remainingForUser,
        };
      });

      setVipPhases(phases);
    } catch (e) {
      console.error('Failed to fetch VIP data:', e);
      setVipPhases([]);
      setCurrentVipPhaseIndex(-1);
    }
  }, [lgoWeb3, walletAddress, tokenDecimals]);

  return {
    vipPhases,
    currentVipPhaseIndex,
    fetchVipData,
    resetVipData,
  };
};
